import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isArray, isEmpty } from 'lodash';

import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';

import { useLiveAccountState } from 'View/live-account/state';
import patchLiveAccount from 'Api/live-account/patchLiveAccount';

import upsertLiveAccountPayload from './model/request/upsertLiveAccountPayload';

const usePatchLiveAccount = ({ resetFormData }) => {
  const { t } = useTranslation('live-account');
  const queryClient = useQueryClient();
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { resetState, updateState, filterState } = useLiveAccountState();

  return useMutation({
    mutationFn: async ({ data, id }) => {
      onShowLoader({ variant: 'loader-primary' });

      const payload = upsertLiveAccountPayload(data);

      return await patchLiveAccount({ payload, id });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['live-account', filterState] });

      onOpenToast({
        status: 'success',
        description: t('updateSuccess'),
        duration: 5,
        alwaysShow: false,
      });

      resetState();
      resetFormData();
    },
    onError: (error) => {
      const errorFields = error?.response?.data?.errorFields;
      const errorMessage = isArray(error?.response?.data?.errors) ? error?.response?.data?.errors[0]?.message : t('updateErrorDescription');

      if (!isEmpty(errorFields)) return updateState({ errorFields });

      updateState({
        isOpenFormUpsert: false,
        errorFields: {},
        errorProps: {
          isOpen: true,
          type: 'error',
          title: t('updateErrorTitle'),
          description: errorMessage,
          onClose: () => updateState({ errorProps: {} }),
          marginBottom: '12px',
        },
      });
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePatchLiveAccount;